import {
  Color,
  FogExp2,
  MathUtils,
  Mesh,
  PMREMGenerator,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector3,
  WebGLRenderer,
  type Texture,
} from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { RENDER, SUN } from '../config';

/**
 * Obloha, slunce, mlha a IBL.
 *
 * Sky je fyzikálně založený atmosférický model (Preetham) — z jedné a té samé
 * oblohy se vygeneruje PMREM environment mapa, takže veškeré ambientní světlo
 * ve scéně sedí k tomu, co hráč reálně vidí nad hlavou. To je největší
 * jednotlivý skok v tom, aby scéna nevypadala jako "three.js demo".
 *
 * Environment mapa se přepočítává jen při změně slunce, ne každý snímek.
 */
export class Environment {
  readonly sky: Sky;
  readonly sunDirection = new Vector3();
  readonly fog: FogExp2;

  private readonly sunDisk: Mesh;
  private readonly pmrem: PMREMGenerator;
  private readonly skyScene = new Scene();
  private envMap: Texture | null = null;

  constructor(
    private readonly scene: Scene,
    renderer: WebGLRenderer,
  ) {
    this.pmrem = new PMREMGenerator(renderer);
    this.pmrem.compileEquirectangularShader();

    this.sky = new Sky();
    this.sky.scale.setScalar(450000);
    this.sky.name = 'sky';

    const uniforms = this.sky.material.uniforms;
    uniforms.turbidity.value = 3.4;
    uniforms.rayleigh.value = 2.1;
    // Mie rozptyl dělá záři kolem slunce. Výchozí hodnoty z three.js dávají
    // v kombinaci s bloomem obří přepálenou kouli přes půl oblohy — nízký
    // koeficient a vysoké g záři stáhnou do těsného halo kolem kotouče.
    uniforms.mieCoefficient.value = 0.0021;
    uniforms.mieDirectionalG.value = 0.94;

    // Obloha dostane vlastní škálu jasu. Bez ní je celý pás kolem slunce
    // po tone mappingu čistě bílý a scéna působí vymytě.
    const skyMaterial = this.sky.material;
    skyMaterial.uniforms.uSkyIntensity = { value: SUN.skyIntensity };
    skyMaterial.fragmentShader = `uniform float uSkyIntensity;
${skyMaterial.fragmentShader}`.replace(
      'gl_FragColor = vec4( texColor, 1.0 );',
      'gl_FragColor = vec4( texColor * uSkyIntensity, 1.0 );',
    );
    skyMaterial.needsUpdate = true;

    scene.add(this.sky);

    this.sunDisk = createSunDisk();
    scene.add(this.sunDisk);

    this.fog = new FogExp2(0x9fb9d4, RENDER.fogDensity);
    scene.fog = this.fog;

    this.setSun(SUN.elevation, SUN.azimuth);
  }

  /**
   * Drží sluneční kotouč u kamery. Slunce je nekonečně daleko, ale geometrie
   * musí zůstat uvnitř far plane, takže ho vozíme s kamerou v pevné vzdálenosti.
   */
  update(camera: PerspectiveCamera): void {
    this.sunDisk.position
      .copy(camera.position)
      .addScaledVector(this.sunDirection, -SUN_DISK_DISTANCE);
    this.sunDisk.quaternion.copy(camera.quaternion);
  }

  /** Nastaví polohu slunce a přegeneruje environment mapu i barvu mlhy. */
  setSun(elevationDeg: number, azimuthDeg: number): void {
    const phi = MathUtils.degToRad(90 - elevationDeg);
    const theta = MathUtils.degToRad(azimuthDeg);
    const sunPosition = new Vector3().setFromSphericalCoords(1, phi, theta);

    this.sky.material.uniforms.sunPosition.value.copy(sunPosition);
    // Směr, kterým světlo dopadá (od slunce do scény).
    this.sunDirection.copy(sunPosition).negate().normalize();

    this.refreshEnvironment();
    this.refreshFog(elevationDeg);
  }

  private refreshEnvironment(): void {
    this.envMap?.dispose();

    // Sky se dočasně přesune do vlastní scény, aby PMREM zachytil jen oblohu.
    this.skyScene.add(this.sky);
    const target = this.pmrem.fromScene(this.skyScene);
    this.scene.add(this.sky);

    this.envMap = target.texture;
    this.scene.environment = this.envMap;
    this.scene.environmentIntensity = SUN.environmentIntensity;
  }

  private refreshFog(elevationDeg: number): void {
    // Mlha musí sedět k horizontu, jinak vzdálené hory "plavou" před oblohou.
    const day = new Color(0x9fb9d4);
    const dusk = new Color(0xd9a583);
    const t = MathUtils.clamp(elevationDeg / 22, 0, 1);
    this.fog.color.copy(dusk).lerp(day, t);
  }

  dispose(): void {
    this.sunDisk.geometry.dispose();
    (this.sunDisk.material as ShaderMaterial).dispose();
    this.envMap?.dispose();
    this.pmrem.dispose();
    this.sky.geometry.dispose();
    this.sky.material.dispose();
  }
}

const SUN_DISK_DISTANCE = 3000;

/**
 * Kotouč slunce.
 *
 * Preethamova obloha sama o sobě dává jen záři — samotný disk v ní po tone
 * mappingu zanikne. Tohle je billboard s velmi vysokou HDR hodnotou v jádru,
 * takže projde přes ACES jako čistě bílý bod a bloom kolem něj udělá záři.
 */
function createSunDisk(): Mesh {
  const material = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uCore: { value: SUN.diskBrightness },
      uGlow: { value: SUN.diskGlow },
      uColor: { value: new Color(1.0, 0.96, 0.88) },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uCore;
      uniform float uGlow;
      uniform vec3 uColor;
      varying vec2 vUv;

      void main() {
        float r = length(vUv - 0.5) * 2.0;
        if (r > 1.0) discard;

        // Ostré jádro plus měkký halo — bloom si zbytek dodělá sám.
        float core = 1.0 - smoothstep(0.30, 0.40, r);
        float halo = pow(1.0 - clamp(r, 0.0, 1.0), 9.0);

        vec3 color = uColor * (core * uCore + halo * uGlow);
        gl_FragColor = vec4(color, clamp(core + halo * 0.7, 0.0, 1.0));
      }
    `,
  });

  const mesh = new Mesh(new PlaneGeometry(SUN.diskSize, SUN.diskSize), material);
  mesh.name = 'sunDisk';
  mesh.frustumCulled = false;
  mesh.renderOrder = 0;
  return mesh;
}
